import { ReactNode, useEffect } from "react";

import { Paper, PaperProps } from "@mantine/core";
import { getTaskListExtension, Link, RichTextEditor } from "@mantine/tiptap";

import "@mantine/tiptap/styles.css";
import { Highlight } from "@tiptap/extension-highlight";
import SubScript from "@tiptap/extension-subscript";
import { Superscript } from "@tiptap/extension-superscript";
import { TaskItem } from "@tiptap/extension-task-item";
import TipTapTaskList from "@tiptap/extension-task-list";
import { TextAlign } from "@tiptap/extension-text-align";
import { Underline } from "@tiptap/extension-underline";
import { Placeholder } from "@tiptap/extensions";
import { type Editor, type Extensions, useEditor } from "@tiptap/react";
import { StarterKit } from "@tiptap/starter-kit";

import classes from "./RichTextField.module.css";

/** Everything the default toolbar can drive. */
const DEFAULT_EXTENSIONS: Extensions = [
  StarterKit,
  Underline,
  Link.configure({
    linkOnPaste: false,
    autolink: true,
    defaultProtocol: "https",
  }),
  Superscript,
  SubScript,
  Highlight,
  TextAlign.configure({ types: ["heading", "paragraph"] }),
  getTaskListExtension(TipTapTaskList),
  TaskItem,
];

export type RichTextFieldProps = PaperProps & {
  value?: string | undefined;
  onChange: (value: string | undefined) => void;
  disabled?: boolean;
  /** Replaces the default toolbar. Pass `RichTextEditor.ControlsGroup`s to keep the editor's
   * styling. Ignored while `showToolbar` is false. */
  toolbar?: ReactNode;
  /**
   * Whether the controls strip renders above the content. Narrow `extensions` when turning it off:
   * a node with no button is still reachable by keyboard shortcut, input rule and paste, so the
   * schema is what holds the document, not the visible controls.
   */
  showToolbar?: boolean;
  /**
   * Replaces the default set, narrowing what the document can hold. Pass this whenever a field
   * narrows its `toolbar`: a hidden control's node stays mounted and reachable by input rule,
   * shortcut or paste. Read once, when the editor mounts.
   */
  extensions?: Extensions;
  /** Shown while the document is empty. Read once, when the editor mounts. */
  placeholder?: string;
  /**
   * Ids of the text describing this field. Goes on the editable element, not the wrapper, so a
   * screen reader reads it on focus. Read once at mount, so the ids must stay stable.
   */
  "aria-describedby"?: string;
  /**
   * Ids of the text naming this field. `Input.Wrapper` only labels Mantine inputs, and the editor
   * is not one, so a caller wrapping this in one points the editor at the label by hand. Read once
   * at mount, so the ids must stay stable.
   */
  "aria-labelledby"?: string;
  /**
   * Appended to the set above, so a caller adds editor behaviour without restating the defaults.
   * Read once, when the editor mounts.
   */
  additionalExtensions?: Extensions;
  /**
   * Rendered under the content, inside the editor's frame, so it can sit against the content's
   * edge. Takes the editor so a caller can read document state; it is `null` until the editor
   * mounts.
   */
  footer?: (editor: Editor | null) => ReactNode;
};

export default function RichTextField({
  style,
  value,
  disabled,
  onChange,
  toolbar,
  showToolbar = true,
  extensions,
  placeholder,
  "aria-describedby": ariaDescribedBy,
  "aria-labelledby": ariaLabelledBy,
  additionalExtensions,
  footer,
  ...props
}: RichTextFieldProps) {
  const editorAttributes = {
    ...(ariaLabelledBy ? { "aria-labelledby": ariaLabelledBy } : {}),
    ...(ariaDescribedBy ? { "aria-describedby": ariaDescribedBy } : {}),
  };

  const editor = useEditor({
    editorProps: Object.keys(editorAttributes).length ? { attributes: editorAttributes } : {},
    extensions: [
      ...(extensions ?? DEFAULT_EXTENSIONS),
      Placeholder.configure({
        // Left undefined, the extension falls back to its own "Write something …" in every
        // editor that passes no placeholder.
        placeholder: placeholder ?? "",
      }),
      ...(additionalExtensions ?? []),
    ],
    content: value,
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      onChange(html);
    },
  });

  useEffect(() => {
    if (!editor) return;

    const nextContent = value ?? "";
    const currentHtml = editor.getHTML();
    const isEditorEmpty = currentHtml === "" || currentHtml === "<p></p>";
    const isValueEmpty = nextContent === "" || nextContent === "<p></p>";

    // TipTap empty docs are `<p></p>`; treat that as empty for controlled sync.
    if (isEditorEmpty && isValueEmpty) return;
    if (nextContent === currentHtml) return;

    editor.commands.setContent(nextContent, { emitUpdate: false });
  }, [value, editor]);

  useEffect(() => {
    if (!editor) return;
    editor.setEditable(!disabled, false);
  }, [editor, disabled]);

  return (
    <Paper {...props} style={style}>
      <RichTextEditor
        editor={editor}
        classNames={{
          toolbar: disabled ? classes.toolbar : undefined,
          control: disabled ? classes.control : undefined,
          controlsGroup: disabled ? classes.controlsGroup : undefined,
          content: disabled ? classes.content : undefined,
        }}
      >
        {showToolbar && (
          <RichTextEditor.Toolbar sticky stickyOffset={60}>
            {toolbar ?? <DefaultToolbarControls />}
          </RichTextEditor.Toolbar>
        )}

        <RichTextEditor.Content />

        {footer?.(editor)}
      </RichTextEditor>
    </Paper>
  );
}

function DefaultToolbarControls() {
  return (
    <>
      <RichTextEditor.ControlsGroup>
        <RichTextEditor.Bold />
        <RichTextEditor.Italic />
        <RichTextEditor.Underline />
        <RichTextEditor.Strikethrough />
        <RichTextEditor.ClearFormatting />
        <RichTextEditor.Highlight />
        <RichTextEditor.Code />
      </RichTextEditor.ControlsGroup>

      <RichTextEditor.ControlsGroup>
        <RichTextEditor.H1 />
        <RichTextEditor.H2 />
        <RichTextEditor.H3 />
        <RichTextEditor.H4 />
      </RichTextEditor.ControlsGroup>

      <RichTextEditor.ControlsGroup>
        <RichTextEditor.Blockquote />
        <RichTextEditor.Hr />
        <RichTextEditor.BulletList />
        <RichTextEditor.OrderedList />
        <RichTextEditor.Subscript />
        <RichTextEditor.Superscript />
      </RichTextEditor.ControlsGroup>

      <RichTextEditor.ControlsGroup>
        <RichTextEditor.AlignLeft />
        <RichTextEditor.AlignCenter />
        <RichTextEditor.AlignJustify />
        <RichTextEditor.AlignRight />
      </RichTextEditor.ControlsGroup>
    </>
  );
}
